import { useMediaQuery } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import React, { MutableRefObject, ReactNode, useMemo, useRef } from "react";

import { ID } from "@holibob-packages/id";

import { ProductPriceFragment } from "../../apiHooks/graphql";
import {
    ProductAdditionListNode,
    ProductAttributeListNode,
    ProductDataCancellationPolicy,
    ProductGuidePriceType,
} from "../../apiHooks/useProduct";
import { FadeUpAnimationWrapper } from "../../components";
import {
    ProductItineraryItemFragment,
    ProductProviderFragment,
    ProductSourceInstanceFragment,
    ProductStartPlaceFragment,
} from "../../gql-request";
import { useNextTranslation } from "../../hooks";
import { useProductNavigation } from "../../hooks/useProductNavigation";
import { useProductPageScrollMarginTop } from "../../hooks/useProductPageScrollMarginTop";
import { ProductContentV2, ProductMeta as ProductMetaType } from "../types";
import { ProductFullV2Wrapper } from "./ProductFullV2Wrapper";
import { ProductGalleryV2, ProductGalleryV2Props } from "./ProductGalleryV2";
import { HeaderSectionV2Props, ProductHeaderSection } from "./ProductHeaderSection";
import { HeaderSectionContent } from "./ProductHeaderSectionContent";
import { ProductInformationWrapper } from "./ProductInformationWrapper";
import { ProductMetadataV2 } from "./ProductMetadataV2";
import { ProductMobileStickyFooter } from "./ProductMobileStickyFooter";
import { ProductNavigationWrapper, ProductNavigationContent } from "./ProductNavigationWrapper";
import { ProductNavigationItem, ProductPageNavigation } from "./ProductPageNavigation";
import { ProductRating } from "./ProductReviewsInfoV2";
import { ProductAdditionsSection } from "./sections/ProductAdditionsSection";
import { ProductCancellationPenaltySection } from "./sections/ProductCancellationPenaltySection";
import { ProductContentSection, useProductContentSections } from "./sections/ProductContentSection";
import { ProductExperienceSection } from "./sections/ProductExperienceSection";
import { ProductGuideLanguagesSection } from "./sections/ProductGuideLanguagesSection";
import { ProductItinerarySection } from "./sections/ProductItinerarySection";
import { ProductLocationSection } from "./sections/ProductLocationSection";
import { ProductOverviewSection } from "./sections/ProductOverviewSection";
import { ProductReviewSection, ProductReviewSectionProps } from "./sections/ProductReviewSection";

export type ProductV2Props = Pick<HeaderSectionV2Props, "title" | "minPrice"> &
    Partial<Pick<ProductGalleryV2Props, "imageList">> & {
        content?: ProductContentV2;
        attributeList?: ProductAttributeListNode[];
        meta: ProductMetaType;
        additions?: ProductAdditionListNode[];
        cancellationPolicy?: ProductDataCancellationPolicy;
        sideElement?: React.ReactNode;
        breadcrumbs?: React.ReactNode;
        reviewRating: number | null;
        reviewCount: number | null;
        minDuration?: string | null;
        maxDuration?: string | null;
        guidePriceType?: ProductGuidePriceType;
        showHeader?: boolean;
        constrainWidth?: boolean;
        withStickyAppBar?: boolean;
        availability?: ReactNode | null;
        withSectionFadeUpAnimation?: boolean;
        itineraryItems: ProductItineraryItemFragment[];
        guidePricePricingData?: ProductPriceFragment["pricingData"];
        startPlace?: ProductStartPlaceFragment | null;
        provider?: ProductProviderFragment | null;
        sourceInstance?: ProductSourceInstanceFragment | null;
        productReview: ProductReviewSectionProps["initialData"];
        showFavouriteButton?: boolean;
        isFavourite?: boolean;
        onFavouriteChange?: (isFavourite: boolean) => void;
        showOperatedBy: boolean;
        showSourcedFrom: boolean;
        scrollableContainerRef?: MutableRefObject<HTMLDivElement | null>;
        showNavigator?: boolean;
        mode?: "full" | "modal";
        productId: ID;
        isOnRequest?: boolean;
    };

export const SECTIONS = {
    OVERVIEW: "OVERVIEW",
    EXPERIENCE: "EXPERIENCE",
    ITINERARY: "ITINERARY",
    LOCATION: "LOCATION",
    ABOUT: "ABOUT",
    ADDITIONS: "ADDITIONS",
    GUIDE_LANGUAGES: "GUIDE_LANGUAGES",
    CANCELLATION_POLICY: "CANCELLATION_POLICY",
    REVIEW: "REVIEW",
};

export function ProductFullV2({
    title,
    minPrice,
    additions,
    cancellationPolicy,
    imageList,
    content,
    meta,
    breadcrumbs,
    reviewRating,
    reviewCount,
    showHeader = true,
    constrainWidth = true,
    withStickyAppBar = false,
    guidePriceType,
    availability,
    withSectionFadeUpAnimation = false,
    itineraryItems,
    guidePricePricingData,
    startPlace,
    attributeList,
    provider,
    sourceInstance,
    productReview,
    showFavouriteButton,
    isFavourite,
    onFavouriteChange,
    showOperatedBy,
    showSourcedFrom,
    scrollableContainerRef,
    showNavigator = true,
    mode = "full",
    productId,
    isOnRequest,
}: ProductV2Props) {
    const [t] = useNextTranslation("product");

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const hasManualItinerary = content?.other.get("ITINERARY")?.some((item) => item.isManual);
    const itineraryType = itineraryItems.length === 0 || hasManualItinerary ? "PRODUCT_CONTENT" : "ITINERARY";

    const isReviewSectionAvailable = productReview.reviewList.length > 0;
    const productContentSections = useProductContentSections(content, {
        showItinerary: itineraryType === "PRODUCT_CONTENT",
    });
    const sections: ProductNavigationItem[] = [
        {
            id: SECTIONS.OVERVIEW,
            label: t("overview.title"),
            isAvailable: !!content?.root?.descriptionJson,
        },
        {
            id: SECTIONS.REVIEW,
            label: t("label.review"),
            isAvailable: isReviewSectionAvailable,
        },
        {
            id: SECTIONS.ITINERARY,
            label: t("itinerary.title"),
            isAvailable: itineraryType === "ITINERARY",
        },
        {
            id: SECTIONS.LOCATION,
            label: t("meetingPoint.label"),
            isAvailable: !!startPlace,
        },
        {
            id: SECTIONS.EXPERIENCE,
            label: t("highlights.experience"),
            isAvailable: true,
        },
        ...productContentSections,
        {
            id: SECTIONS.ADDITIONS,
            label: t("label.extraAvailable"),
            isAvailable: !!additions?.length,
        },
        {
            id: SECTIONS.GUIDE_LANGUAGES,
            label: t("label.guideLanguages"),
            isAvailable: !!meta.guideLanguages?.length,
        },
        {
            id: SECTIONS.CANCELLATION_POLICY,
            label: t("label.cancellationPolicy"),
            isAvailable: !!cancellationPolicy,
        },
    ];

    const headerRef = useRef<HTMLDivElement | null>(null);
    const scrollMarginTop = useProductPageScrollMarginTop(headerRef, withStickyAppBar, scrollableContainerRef);
    const { activeNavigationItem, navigationItems, onManualNavigationChange, onSectionVisibilityChange } =
        useProductNavigation({ sections, scrollMarginTop, scrollableContainerRef });

    const isMobileModalMode = isMobile && mode === "modal";

    const productRatingComponent = useMemo(
        () => (
            <ProductRatingWrapper data-is-clickable={isReviewSectionAvailable}>
                <ProductRating
                    reviewRating={reviewRating ?? 0}
                    reviewCount={reviewCount ?? 0}
                    onClick={() => onManualNavigationChange(SECTIONS.REVIEW)}
                />
            </ProductRatingWrapper>
        ),
        [reviewRating, reviewCount, onManualNavigationChange, isReviewSectionAvailable]
    );

    return (
        <>
            <Wrapper data-mode={mode}>
                <ProductFullV2Wrapper
                    constrainWidth={!isMobileModalMode && constrainWidth}
                    showNavigator={showNavigator}
                >
                    <ProductGalleryStyled
                        data-testid="product-gallery"
                        imageList={imageList ?? []}
                        seeAllLabel={t("gallery.seeAllImages.label")}
                        showFavouriteButton={showFavouriteButton}
                        isFavourite={isFavourite}
                        onFavouriteChange={onFavouriteChange}
                        maxWidthOnDesktop={1200}
                        borderRounded={!isMobileModalMode}
                    />
                </ProductFullV2Wrapper>

                <ProductHeaderSection
                    showHeader={showHeader}
                    title={title}
                    minPrice={minPrice}
                    withStickyAppBar={withStickyAppBar}
                    cancellationPolicy={cancellationPolicy}
                    guidePriceType={guidePriceType}
                    availability={availability}
                    breadcrumbs={breadcrumbs}
                    guidePricePricingData={guidePricePricingData}
                    ref={headerRef}
                    ContentComponent={HeaderSectionContent}
                    productRatingComponent={productRatingComponent}
                />

                <ProductFullV2Wrapper
                    constrainWidth={constrainWidth}
                    showNavigator={showNavigator}
                    data-ref="product-content"
                >
                    {showNavigator && (
                        <ProductNavigationWrapper>
                            <ProductNavigationContent style={{ top: scrollMarginTop }}>
                                <ProductPageNavigation
                                    items={navigationItems}
                                    activeItemId={activeNavigationItem}
                                    onChange={(_event, itemId) => onManualNavigationChange(itemId)}
                                />
                            </ProductNavigationContent>
                        </ProductNavigationWrapper>
                    )}
                    <ProductInformationWrapper>
                        <FadeUpAnimationWrapper isEnabled={withSectionFadeUpAnimation}>
                            <ProductMetadataV2
                                meta={meta}
                                cancellationPolicy={cancellationPolicy}
                                attributeList={attributeList}
                                isOnRequest={isOnRequest}
                            />
                        </FadeUpAnimationWrapper>
                        {!!content?.root?.descriptionJson && (
                            <ProductOverviewSection
                                id={SECTIONS.OVERVIEW}
                                onVisibilityChange={onSectionVisibilityChange}
                                withSectionFadeUpAnimation={withSectionFadeUpAnimation}
                                descriptionJson={content.root.descriptionJson}
                                providerName={provider?.name}
                                sourceInstanceName={sourceInstance?.name}
                                showOperatedBy={showOperatedBy}
                                showSourcedFrom={showSourcedFrom}
                                scrollMarginTop={scrollMarginTop}
                            />
                        )}
                        {productReview.reviewList.length > 0 && (
                            <ProductReviewSection
                                id={SECTIONS.REVIEW}
                                withSectionFadeUpAnimation={withSectionFadeUpAnimation}
                                onVisibilityChange={onSectionVisibilityChange}
                                productId={productId}
                                initialData={productReview}
                                scrollMarginTop={scrollMarginTop}
                            />
                        )}
                        {itineraryType === "ITINERARY" && (
                            <ProductItinerarySection
                                id={SECTIONS.ITINERARY}
                                onVisibilityChange={onSectionVisibilityChange}
                                withSectionFadeUpAnimation={withSectionFadeUpAnimation}
                                withBottomBorder={true}
                                items={itineraryItems}
                                scrollMarginTop={scrollMarginTop}
                            />
                        )}
                        {startPlace && (
                            <ProductLocationSection
                                id={SECTIONS.LOCATION}
                                onVisibilityChange={onSectionVisibilityChange}
                                withSectionFadeUpAnimation={withSectionFadeUpAnimation}
                                withBottomBorder={true}
                                startPlace={startPlace}
                                startTimes={meta.startTimes}
                                content={content}
                                scrollMarginTop={scrollMarginTop}
                            />
                        )}
                        <ProductExperienceSection
                            id={SECTIONS.EXPERIENCE}
                            onVisibilityChange={onSectionVisibilityChange}
                            withSectionFadeUpAnimation={withSectionFadeUpAnimation}
                            highlights={content?.other.get("HIGHLIGHT")}
                            imageList={imageList ?? []}
                            scrollMarginTop={scrollMarginTop}
                        />
                        {productContentSections.map((section) => {
                            const { isAvailable } = section;
                            const entries = content?.other.get(section.id);
                            if (!isAvailable || !entries?.length) return null;

                            return (
                                <ProductContentSection
                                    key={section.id}
                                    id={section.id}
                                    type={section.id}
                                    onVisibilityChange={onSectionVisibilityChange}
                                    entries={entries}
                                    withSectionFadeUpAnimation={withSectionFadeUpAnimation}
                                    withBottomBorder={true}
                                    scrollMarginTop={scrollMarginTop}
                                />
                            );
                        })}
                        {!!additions?.length && (
                            <ProductAdditionsSection
                                id={SECTIONS.ADDITIONS}
                                onVisibilityChange={onSectionVisibilityChange}
                                additions={additions}
                                withSectionFadeUpAnimation={withSectionFadeUpAnimation}
                                withBottomBorder={true}
                                scrollMarginTop={scrollMarginTop}
                            />
                        )}
                        {!!meta.guideLanguages?.length && (
                            <ProductGuideLanguagesSection
                                id={SECTIONS.GUIDE_LANGUAGES}
                                onVisibilityChange={onSectionVisibilityChange}
                                guideLanguages={meta.guideLanguages}
                                withSectionFadeUpAnimation={withSectionFadeUpAnimation}
                                withBottomBorder={true}
                                scrollMarginTop={scrollMarginTop}
                            />
                        )}
                        {cancellationPolicy && (
                            <ProductCancellationPenaltySection
                                id={SECTIONS.CANCELLATION_POLICY}
                                onVisibilityChange={onSectionVisibilityChange}
                                penaltyList={cancellationPolicy.penaltyList.nodes}
                                withSectionFadeUpAnimation={withSectionFadeUpAnimation}
                                scrollMarginTop={scrollMarginTop}
                            />
                        )}
                    </ProductInformationWrapper>
                </ProductFullV2Wrapper>
            </Wrapper>

            <ProductMobileStickyFooter
                availability={availability}
                guidePriceType={guidePriceType}
                minPrice={minPrice}
            />
        </>
    );
}

const ProductRatingWrapper = styled("div")({
    "&[data-is-clickable='true']": {
        "&:hover": {
            cursor: "pointer",
        },
    },
});

const Wrapper = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",

    [theme.breakpoints.up("md")]: {
        "& [data-ref='product-header-section']": {
            order: -1,
        },
    },

    "&& p[data-size='large']": {
        lineHeight: 1.25,
        letterSpacing: 0.15,
    },

    "&& ul": {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(0.5),
    },
}));

const ProductGalleryStyled = styled(ProductGalleryV2)({
    gridArea: "gallery",
});
